import { composeThemeFromProps, Compose } from '@css-modules-theme/react';
import clsx from 'clsx';
import { forwardRef, useRef, Children } from 'react';
import { Loader } from 'react-feather';
import mergeRefs from 'react-merge-refs';
import defaultStyles from './styles/Button.module.scss.js';
import { jsxs, Fragment, jsx } from 'react/jsx-runtime';
import { Link } from '../link/Link.js';
import { omit } from '../../utils/omit.js';

function processChildren(children) {
  const childrenList = [];
  let isPrevChildPure = false;
  Children.forEach(children, child => {
    const isCurrentChildPure = typeof child === 'string' || typeof child === 'number';
    if (isCurrentChildPure && isPrevChildPure) {
      const lastIndex = childrenList.length - 1;
      const lastChild = childrenList[lastIndex];
      childrenList[lastIndex] = `${lastChild}${child}`;
    } else {
      childrenList.push(child);
    }
    isPrevChildPure = isCurrentChildPure;
  });
  return Children.map(childrenList, child => typeof child === 'string' ? /*#__PURE__*/jsx("span", {
    children: child
  }) : child);
}
const Button = /*#__PURE__*/forwardRef((props, ref) => {
  const {
    style,
    theme,
    children,
    htmlType = 'button',
    variant = 'solid',
    status = 'primary',
    ariaLabel,
    size = 'md',
    shape,
    href,
    anchorProps,
    disabled,
    loading,
    loadingIcon = /*#__PURE__*/jsx(Loader, {}),
    loadingFixedWidth,
    icon,
    iconOnly,
    iconAlignment = 'left',
    onClick,
    fluid,
    flat,
    role = 'button',
    noHover = false,
    dataTestId,
    showContentWhileLoading = true,
    ...rest
  } = props;
  const styles = composeThemeFromProps(defaultStyles, {
    theme
  }, {
    compose: Compose.Merge
  });
  const iconNode = loading ? loadingIcon : icon;
  const buttonRef = useRef();
  const testProps = {
    'data-test-id': dataTestId
  };
  const isLink = variant === 'link';
  const statusClasses = {
    [styles.primary]: status === 'primary',
    [styles.secondary]: status === 'secondary',
    [styles.warning]: status === 'warning',
    [styles.error]: status === 'error',
    [styles.info]: status === 'info',
    [styles.accent]: status === 'accent',
    [styles.success]: status === 'success'
  };
  const sizeClasses = !isLink && {
    [styles.xs]: size === 'xs',
    [styles.sm]: size === 'sm',
    [styles.md]: size === 'md',
    [styles.lg]: size === 'lg'
  };
  const variantClasses = {
    [styles.solid]: variant === 'solid',
    [styles.outline]: variant === 'outline',
    [styles.dashed]: variant === 'dashed',
    [styles.ghost]: variant === 'ghost',
    [styles.link]: isLink
  };
  const shapeClasses = {
    [styles.circle]: shape === 'circle',
    [styles.square]: shape === 'square',
    [styles.rounded]: shape === 'rounded'
  };
  const buttonClassNames = clsx(styles.root, statusClasses, sizeClasses, variantClasses, shapeClasses, {
    [styles.flat]: flat === true,
    [styles.raised]: flat === false,
    [styles.fluid]: fluid,
    [styles.loading_fixed_width]: loadingFixedWidth,
    [styles.loading]: loading,
    [styles.labelHiddenOnLoad]: loading && !showContentWhileLoading,
    [styles.icon]: iconOnly || !children && children !== 0 && iconNode,
    [styles.disabled]: disabled
  });
  const handleClick = event => {
    if (loading) {
      typeof event?.preventDefault === 'function' && event.preventDefault();
      return;
    }
    onClick && onClick(event);
  };
  const InnerContent = iconAlignment === 'left' ? /*#__PURE__*/jsxs(Fragment, {
    children: [iconNode, processChildren(children)]
  }) : /*#__PURE__*/jsxs(Fragment, {
    children: [processChildren(children), iconNode]
  });
  if (href && !disabled) {
    const _anchorProps = {
      ...anchorProps,
      to: href
    };
    return /*#__PURE__*/jsx(Link, {
      ...rest,
      ..._anchorProps,
      ref: mergeRefs([buttonRef, ref]),
      "aria-disabled": disabled,
      "aria-label": ariaLabel,
      style: style,
      className: buttonClassNames,
      onClick: handleClick,
      ...testProps,
      children: InnerContent
    });
  }
  const Component = 'button';
  return /*#__PURE__*/jsx(Component, {
    ...omit(rest, ['classnames']),
    ref: mergeRefs([buttonRef, ref]),
    style: style,
    "aria-label": ariaLabel,
    className: buttonClassNames,
    "aria-disabled": disabled || loading,
    type: htmlType,
    disabled: disabled || loading,
    role: role,
    onClick: handleClick,
    "data-hover": noHover ? 'nohover' : null,
    ...testProps,
    children: InnerContent
  });
});
Button.displayName = 'Button';

export { Button };
